import DownloadIcon from '@mui/icons-material/Download';
import { Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { downloadConnectorPackage, listConnectorPackages } from '../../api/business/endpoints';
import type { ConnectorPackage } from '../../api/business/types';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatNumber } from '../../components/format';
import { saveBlob } from '../../components/saveBlob';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * The connectors to install in a shop (R0035): one per platform version line the licence has, the version
 * released with this app. Nothing is shown when the licence has no e-shop integration.
 */
export const ConnectorDownloads = ({ companyId }: { companyId: string }) => {
  const { language, t } = useLanguage();
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['connector-packages', companyId],
    queryFn: () => listConnectorPackages(companyId),
  });

  const download = async (item: ConnectorPackage): Promise<void> => {
    setBusy(item.file);
    setFailure(null);

    try {
      saveBlob(await downloadConnectorPackage(companyId, item.file), item.file);
    } catch (error: unknown) {
      setFailure(error);
    } finally {
      setBusy(null);
    }
  };

  if (query.data !== undefined && query.data.length === 0) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 3 }}>
      <Stack spacing={0.5} sx={{ p: 2 }}>
        <Typography variant="h6">{t('connectors.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('connectors.subtitle')}
        </Typography>
      </Stack>
      <ErrorNotice error={query.error ?? failure} onRetry={() => void query.refetch()} />
      <Stack divider={<Divider flexItem />}>
        {(query.data ?? []).map((item) => (
          <Stack
            key={item.file}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            sx={{ px: 2, py: 1.5 }}
          >
            <Stack sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2">
                {t(`platform.${item.platform}`)} {item.platformVersions.min} – {item.platformVersions.max}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('connectors.version')} {item.version} ·{' '}
                {formatNumber(Math.ceil(item.bytes / 1024), language)} kB
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={busy !== null}
              onClick={() => void download(item)}
            >
              {t('connectors.download')}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};
